import { useContext, useEffect, useRef, useState } from "react";
import app from "nystem";
import { Button, DatabaseSearchContext } from "nystem-components";

const csvRow = (values) =>
  `${values
    .map((value) => {
      const text = String(value ?? "");
      return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
    })
    .join(",")}\r\n`;

const ExportResultCsv = ({ model, view }) => {
  const { search, loading } = useContext(DatabaseSearchContext);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setMessage("");
    setError("");
  }, [search]);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button
        className={model.className}
        disabled={exporting || loading || !search.searchTotal}
        aria-disabled={exporting || loading || !search.searchTotal}
        onClick={async () => {
          if (exporting || loading || !search.searchTotal) return;
          setExporting(true);
          setError("");
          setMessage("Preparing CSV…");
          try {
            const rows = [
              "\uFEFF",
              csvRow([
                "image_id",
                "filename",
                "source_full_path",
                "side",
                "academic_year",
                "production_status",
                "production_qc_flagged",
                "record",
                "register_number",
                "pupil_number",
                "name_transcription",
                "forenames",
                "surname",
                "date_of_birth",
                "guardian",
              ]),
            ];
            const uploads = new Map();
            let record = 0;
            let position = 0;
            let searchTotal = search.searchTotal;
            while (position < searchTotal) {
              const result = await view.event("search", { ...search, data: undefined, position });
              if (!mounted.current) return;
              if (result.error || !result.data) throw new Error(result.error || "Could not load search results.");
              searchTotal = result.searchTotal;
              if (!result.data.length) break;
              for (const data of result.data) {
                if (!data.pupils?.length) continue;
                if (data.sourceUpload && !uploads.has(data.sourceUpload)) {
                  const upload = await app.database.upload.get({ id: data.sourceUpload });
                  if (!mounted.current) return;
                  if (upload.error) throw new Error(upload.error);
                  uploads.set(data.sourceUpload, upload.data);
                }
                const images = uploads.get(data.sourceUpload)?.images || [];
                const pdf = images.find((image) => image.ext?.toLowerCase() === "pdf");
                const sourceImages = images.filter((image) =>
                  ["jpg", "jpeg", "png"].includes(image.ext?.toLowerCase()),
                );
                for (const pupil of data.pupils) {
                  const nameImage =
                    !pdf && Number.isInteger(pupil.nameImageIndex) && sourceImages[pupil.nameImageIndex];
                  const nameParts = (pupil.fullName || "").split("|").map((part) => part.trim());
                  rows.push(
                    csvRow([
                      nameImage ? nameImage.id : "",
                      pdf && Number.isInteger(pupil.nameImageIndex)
                        ? pdf.name || `${pdf.id}.${pdf.ext}`
                        : nameImage
                          ? nameImage.name || `${nameImage.id}.${nameImage.ext}`
                          : "",
                      "",
                      "",
                      data.document?.reportingYear,
                      "",
                      "",
                      ++record,
                      "",
                      pupil.rowNumber,
                      pupil.fullName,
                      nameParts.length === 2 ? nameParts[1] : "",
                      nameParts.length === 2 ? nameParts[0] : "",
                      pupil.birthDate,
                      [pupil.guardian, pupil.guardianDetails].filter(Boolean).join(" / "),
                    ]),
                  );
                }
              }
              position += result.data.length;
              setMessage(`Preparing CSV… ${Math.min(position, searchTotal)} of ${searchTotal} results`);
              await new Promise((resolve) => setTimeout(resolve, 0));
              if (!mounted.current) return;
            }
            if (!record) {
              setMessage("No pupils in the current search results.");
              return;
            }
            const url = URL.createObjectURL(new Blob(rows, { type: "text/csv;charset=utf-8" }));
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = "results.csv";
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            setMessage(`Exported ${record} pupil rows.`);
          } catch (error) {
            if (mounted.current) {
              setMessage("");
              setError(error.message || "Could not export the current search results.");
            }
          } finally {
            if (mounted.current) setExporting(false);
          }
        }}
      >
        {exporting ? "Exporting…" : model.text || "Export CSV"}
      </Button>
      {message && (
        <span role="status" className="text-sm text-gray-600">
          {message}
        </span>
      )}
      {error && (
        <span role="alert" className="text-sm text-red-700">
          {error}
        </span>
      )}
    </div>
  );
};

export default ExportResultCsv;
